import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ExternalLink, Users, Trash2, BookmarkCheck, AlertCircle, BarChart3 } from 'lucide-react'
import { useSavedInfluencers } from '@/hooks/use-saved-influencers'
import { useToast } from '@/hooks/use-toast'
import { Skeleton } from '@/components/ui/skeleton'
import type { SavedInfluencerProfile } from '@/types'

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

interface SavedInfluencerCardProps {
  profile: SavedInfluencerProfile
  onDelete: (saveId: string) => Promise<void>
}

function SavedInfluencerCard({ profile, onDelete }: SavedInfluencerCardProps) {
  const [isDeleting, setIsDeleting] = useState(false)
  const { toast } = useToast()
  const navigate = useNavigate()

  const formatFollowers = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`
    return count.toString()
  }

  const trimSnippet = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength).trim() + '...'
  }

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const extractUsername = (url: string) => {
    // Extract username from Instagram URL (e.g., https://instagram.com/username)
    const match = url.match(/instagram\.com\/([^/?]+)/)
    return match ? match[1] : ''
  }

  const handleAnalyze = () => {
    const username = extractUsername(profile.url)
    if (username) {
      navigate(`/request-analytics?username=${username}`)
    } else {
      toast({
        title: "Error",
        description: "Could not extract username from Instagram URL",
        variant: "destructive",
      })
    }
  }

  const handleDelete = async () => {
    if (isDeleting) return

    setIsDeleting(true)
    try {
      await onDelete(profile.save_id)
      toast({
        title: "Profile Removed",
        description: `${profile.title} has been removed from your saved collection.`,
      })
    } catch (error) {
      console.error('Failed to delete saved influencer:', error)
      toast({
        title: "Delete Failed",
        description: "Failed to remove the profile. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <TooltipProvider>
      <motion.div variants={itemVariants}>
        <Card className="group hover:shadow-md transition-all duration-200 border border-amber-200/20 dark:border-amber-800/20">
          <CardHeader className="pb-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 flex-shrink-0">
                <BookmarkCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base font-semibold group-hover:text-primary transition-colors line-clamp-2">
                    {profile.title}
                  </CardTitle>
                  <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 shrink-0">
                    Saved
                  </Badge>
                </div>
                <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                  <Users className="h-3 w-3" />
                  <span className="font-medium">{formatFollowers(profile.followers)} followers</span>
                  <span className="text-xs">• Saved {formatDate(profile.timestamp)}</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">
              {trimSnippet(profile.snippet)}
            </p>
          </CardHeader>
          
          <CardContent className="pt-0">
            <div className="flex gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button asChild size="sm" variant="outline" className="flex-1">
                    <a href={profile.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-2 h-3 w-3" />
                      View
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View Instagram profile</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    size="sm" 
                    onClick={handleAnalyze}
                    className="flex-1"
                  >
                    <BarChart3 className="mr-2 h-3 w-3" />
                    Analyze
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Request analytics for this profile</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="mr-2 h-3 w-3" />
                    {isDeleting ? 'Removing...' : 'Remove'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Remove from saved collection</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </TooltipProvider>
  )
}

export function SavedButUnanalyzed() {
  const { savedInfluencers, isLoading, error, deleteSavedInfluencer } = useSavedInfluencers()

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 flex-shrink-0">
              <BookmarkCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">Saved Profiles</CardTitle>
              <CardDescription>
                Profiles you've saved but haven't analyzed yet
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-[200px]" />
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-3 w-[60px]" />
                        <Skeleton className="h-3 w-[80px]" />
                      </div>
                    </div>
                  </div>
                  <Skeleton className="h-5 w-12" />
                </div>
                <Skeleton className="h-3 w-full mt-2" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex gap-2">
                  <Skeleton className="h-8 flex-1" />
                  <Skeleton className="h-8 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (error) {
    return (
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 flex-shrink-0">
              <BookmarkCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">Saved Profiles</CardTitle>
              <CardDescription>
                Profiles you've saved but haven't analyzed yet
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-center py-8">
          <div className="p-3 rounded-lg bg-destructive/10 w-fit mx-auto mb-3">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    )
  }

  // Empty state
  if (savedInfluencers.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 flex-shrink-0">
              <BookmarkCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">Saved Profiles</CardTitle>
              <CardDescription>
                Profiles you've saved but haven't analyzed yet
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-center py-8">
          <div className="p-3 rounded-lg bg-muted/50 w-fit mx-auto mb-3">
            <BookmarkCheck className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">
            Save influencer profiles from search results to build your collection
          </p>
        </CardContent>
      </Card>
    )
  }

  // Profiles list
  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 flex-shrink-0">
            <BookmarkCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold">Saved Profiles</CardTitle>
            <div className="flex items-center gap-2 mt-1">
              <CardDescription>
                {savedInfluencers.length} profile{savedInfluencers.length !== 1 ? 's' : ''} saved but not analyzed
              </CardDescription>
              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                {savedInfluencers.length}
              </Badge>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <motion.div 
          initial="hidden"
          animate="visible"
          transition={{ staggerChildren: 0.05 }}
          className="space-y-3"
        >
          {savedInfluencers.map((profile) => (
            <SavedInfluencerCard
              key={profile.save_id}
              profile={profile}
              onDelete={deleteSavedInfluencer}
            />
          ))}
        </motion.div>
      </CardContent>
    </Card>
  )
} 